import Guid from "_shared/guids/guid";
import _ from "lodash";
import Session from "users/__session/session";
import ProposalReviewerCommand from "work/proposals/reviewers/_api/command-contracts/proposal-reviewer-command";
import ReplaceableDocumentAPIRequest from "work/proposals/_api/commands/replaceable-document-api-request";
import WorkProposalAPIRequest from "work/proposals/_api/commands/work-proposal-api-request";
import WorkTeamAPICommand from "work/proposals/_api/commands/work-team-api-command";
import Proposal from "work/proposals/proposal";
import FeeScheduleCategoryCommand from "marketplace/teams/fee-schedules/_api/commands/fee-schedule-category-command";
import { FeeScheduleCategoryMaskedCurrency, Masked, MaskedFeeSchedule } from "work/_shared/masked-fields";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import { ProposalBuilderJSON } from "work/proposals/_schemas/proposal-builder-schema";

class SaveProposalAPIRequest {
  public payload: Array<{ op: string; path: string; value: unknown }> = [];

  public constructor(params: { updatedProposal: Proposal; builderJSON?: ProposalBuilderJSON; session: Session }) {
    const { updatedProposal, builderJSON, session } = params;

    if (!session.user) {
      throw new Error("User session is required to create SaveProposalAPIRequest");
    }

    const redlinedRevision = updatedProposal.redlinedRevision;
    if (
      redlinedRevision &&
      (!updatedProposal.userRevision ||
        !redlinedRevision?.isEquivalentTo(
          new Proposal({
            currentUser: session.user,
            spec: { ...updatedProposal.userRevision, clientReviewers: [], vendorReviewers: [] },
          }),
        ))
    ) {
      this.payload.push({
        op: "replace",
        path: "/userRevision",
        value: new WorkProposalAPIRequest(redlinedRevision, session).payload,
      });
    }

    if (builderJSON && !updatedProposal.isSubmitted) {
      this.payload.push({
        op: "replace",
        path: "/userBuilderJSON",
        value: builderJSON,
      });
    }
  }
}

class ShareProposalAPIRequest {
  public payload: Array<{ op: string; path: string; value: unknown }> = [];
  private readonly session: Session;

  public constructor(params: { proposal: Proposal; session: Session }) {
    const { proposal, session } = params;
    const updatedProposal = proposal;
    const originalProposal = proposal.originalProposal;
    this.session = session;

    this.payload.push(
      {
        op: "replace",
        path: "/userRevision",
        value: null,
      },
      {
        op: "replace",
        path: "/userBuilderJSON",
        value: null,
      },
    );

    if (!updatedProposal.isSubmitted) {
      this.populateProposalPATCHOperations(updatedProposal, originalProposal);
      return;
    }

    this.populatePartyRevisionPATCHOperations(updatedProposal, originalProposal);
  }

  private populatePartyRevisionPATCHOperations(updatedProposal: Proposal, originalProposal: Proposal) {
    if (!this.session.user) {
      throw new Error("User session is required to create ShareProposalAPIRequest");
    }
    const redlinedRevision = updatedProposal.redlinedRevision;
    if (
      originalProposal.partyRevision &&
      redlinedRevision &&
      !redlinedRevision?.isEquivalentTo(
        new Proposal({
          currentUser: this.session.user,
          spec: { ...originalProposal.partyRevision, clientReviewers: [], vendorReviewers: [] },
        }),
      )
    ) {
      this.populateProposalPATCHOperations(
        redlinedRevision,
        new Proposal({
          currentUser: this.session.user,
          spec: { ...originalProposal.partyRevision, clientReviewers: [], vendorReviewers: [] },
        }),
        "/partyRevision",
      );
    }
  }

  private populateProposalPATCHOperations(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix = "",
  ): void {
    if (updatedProposal.name?.value !== originalProposal.name?.value)
      this.payload = [{ op: "replace", path: `${pathPrefix}/name`, value: updatedProposal.name }, ...this.payload];

    if (updatedProposal.description?.value !== originalProposal.description?.value)
      this.payload = [
        { op: "replace", path: `${pathPrefix}/description`, value: updatedProposal.description },
        ...this.payload,
      ];

    if (!updatedProposal.negotiable.isEqualTo(originalProposal.negotiable)) {
      this.payload = [
        { op: "replace", path: `${pathPrefix}/negotiable`, value: updatedProposal.negotiable.value },
        ...this.payload,
      ];
    }
    this.populateProposalDateDiffPatches(updatedProposal, originalProposal, pathPrefix);
    this.populateProposalReviewerDiffPatches(updatedProposal, originalProposal, pathPrefix);
    this.populateProposalTeamDiffPatches(updatedProposal, originalProposal, pathPrefix);
    this.populateFeeScheduleDiffPatches(originalProposal.feeSchedule, updatedProposal.feeSchedule, pathPrefix);
    this.populateProposalContractsDiffPatches(updatedProposal, originalProposal, pathPrefix);
    this.populateProposalDiscountDiffPatch(updatedProposal, originalProposal, pathPrefix);
    this.populateProposalTemplateDiffPatches(updatedProposal, originalProposal, pathPrefix);
  }

  private populateProposalDateDiffPatches(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    if (
      (updatedProposal.responseDueBy || originalProposal.responseDueBy) &&
      !updatedProposal.responseDueBy?.isSame(originalProposal.responseDueBy)
    ) {
      this.payload = [
        { op: "replace", path: `${pathPrefix}/responseDueBy`, value: updatedProposal.responseDueBy },
        ...this.payload,
      ];
    }

    if (
      (updatedProposal.startDate || originalProposal.startDate) &&
      !updatedProposal.startDate?.isSame(originalProposal.startDate)
    ) {
      this.payload = [
        { op: "replace", path: `${pathPrefix}/startDate`, value: updatedProposal.startDate },
        ...this.payload,
      ];
    }

    if (
      (updatedProposal.endDate || originalProposal.endDate) &&
      !updatedProposal.endDate?.isSame(originalProposal.endDate)
    ) {
      this.payload = [
        { op: "replace", path: `${pathPrefix}/endDate`, value: updatedProposal.endDate },
        ...this.payload,
      ];
    }
  }

  private populateProposalReviewerDiffPatches(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    if (!_.isEqual(updatedProposal.clientReviewers, originalProposal.clientReviewers)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/clientReviewers`,
          value: updatedProposal.clientReviewers.map((reviewer) => new ProposalReviewerCommand(reviewer).payload),
        },
        ...this.payload,
      ];
    }

    if (!_.isEqual(updatedProposal.vendorReviewers, originalProposal.vendorReviewers)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/vendorReviewers`,
          value: updatedProposal.vendorReviewers.map((reviewer) => new ProposalReviewerCommand(reviewer).payload),
          ...this.payload,
        },
      ];
    }
  }

  private populateProposalTeamDiffPatches(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    if (
      updatedProposal.vendorTeam &&
      Masked.isNotMasked(updatedProposal.vendorTeam) &&
      Masked.isNotMasked(originalProposal.vendorTeam) &&
      !updatedProposal.vendorTeam.isEqualTo(originalProposal.vendorTeam)
    ) {
      const update = {
        op: "replace",
        path: `${pathPrefix}/vendorTeam`,
        value: WorkTeamAPICommand.fromTeam(updatedProposal.vendorTeam).payload,
      };
      this.payload = this.payload?.length ? this.payload.concat(update) : [update];
    }
    if (
      Masked.isNotMasked(updatedProposal.teamRestricted) &&
      Masked.isNotMasked(originalProposal.teamRestricted) &&
      !updatedProposal.teamRestricted.isEqualTo(originalProposal.teamRestricted)
    ) {
      const update = {
        op: "replace",
        path: `${pathPrefix}/teamRestricted`,
        value: updatedProposal.teamRestricted,
      };
      this.payload = this.payload?.length ? this.payload.concat(update) : [update];
    }
  }

  private populateFeeScheduleDiffPatches(
    originalFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule,
    updatedFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule,
    pathPrefix: string,
  ): void {
    if (Masked.isMasked(originalFeeSchedule) || Masked.isMasked(updatedFeeSchedule)) {
      return;
    }

    if (
      !updatedFeeSchedule.some((cat) => Masked.isNotMasked(cat)) &&
      !originalFeeSchedule.some((cat) => Masked.isNotMasked(cat))
    ) {
      const newFeeSchedule = updatedFeeSchedule.map(
        (feeSchedule) => new FeeScheduleCategoryCommand(feeSchedule).payload,
      );

      const update = {
        op: "replace",
        path: `${pathPrefix}/feeSchedule`,
        value: newFeeSchedule,
      };
      this.payload = [update, ...this.payload];
    } else {
      for (const updatedCategory of updatedFeeSchedule) {
        const existingCategory = originalFeeSchedule.find((origCategory) =>
          origCategory.id.isEqualTo(updatedCategory.id),
        );

        if (!existingCategory) {
          this.payload = [
            {
              op: "add",
              path: `${pathPrefix}/feeSchedule`,
              value: new FeeScheduleCategoryCommand(updatedCategory).payload,
            },
            ...this.payload,
          ];
          continue;
        }

        if (existingCategory.isEqualTo(updatedCategory)) continue;

        if (
          FeeScheduleCategoryMaskedCurrency.isNotMasked(updatedCategory) &&
          FeeScheduleCategoryMaskedCurrency.isNotMasked(existingCategory)
        ) {
          const update = {
            op: "replace",
            path: `${pathPrefix}/feeSchedule/${existingCategory.id.value}`,
            value: new FeeScheduleCategoryCommand(updatedCategory).payload,
          };
          this.payload = this.payload?.length ? this.payload.concat(update) : [update];
        } else {
          this.populateFeeScheduleSubfieldDiffPatches(existingCategory, updatedCategory, pathPrefix);
        }
      }
    }
  }

  private populateFeeScheduleSubfieldDiffPatches(
    existingCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
    updatedCategory: FeeScheduleCategoryMaskedCurrency,
    pathPrefix: string,
  ): void {
    if (!existingCategory.name.isEqualTo(updatedCategory.name)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/feeSchedule/${existingCategory.id.value}/name`,
          value: updatedCategory.name,
        },
        ...this.payload,
      ];
    }

    if (
      (existingCategory.description && !updatedCategory.description) ||
      (!existingCategory.description && updatedCategory.description) ||
      !existingCategory.description?.isEqualTo(updatedCategory.description)
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/feeSchedule/${existingCategory.id.value}/description`,
          value: updatedCategory.description,
        },
        ...this.payload,
      ];
    }

    if (
      (existingCategory.billingCode && !updatedCategory.billingCode) ||
      (!existingCategory.billingCode && updatedCategory.billingCode) ||
      !existingCategory.billingCode?.isEqualTo(updatedCategory.billingCode)
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/feeSchedule/${existingCategory.id.value}/billingCode`,
          value: updatedCategory.billingCode,
        },
        ...this.payload,
      ];
    }
  }

  private populateProposalContractsDiffPatches(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    if (
      Masked.isNotMasked(updatedProposal.clientPolicies) &&
      Masked.isNotMasked(originalProposal.clientPolicies) &&
      !_.isEqual(updatedProposal.clientPolicies, originalProposal.clientPolicies)
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/clientPolicies`,
          value: updatedProposal.clientPolicies.map(
            (replaceAbleDocument) => new ReplaceableDocumentAPIRequest(replaceAbleDocument).payload,
          ),
        },
        ...this.payload,
      ];
    }

    if (
      Masked.isNotMasked(updatedProposal.vendorPolicies) &&
      Masked.isNotMasked(originalProposal.vendorPolicies) &&
      !_.isEqual(updatedProposal.vendorPolicies, originalProposal.vendorPolicies)
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/vendorPolicies`,
          value: updatedProposal.vendorPolicies.map(
            (replaceAbleDocument) => new ReplaceableDocumentAPIRequest(replaceAbleDocument).payload,
          ),
        },
        ...this.payload,
      ];
    }

    if (
      Masked.isNotMasked(updatedProposal.conflicts) &&
      Masked.isNotMasked(originalProposal.conflicts) &&
      !_.isEqual(updatedProposal.conflicts, originalProposal.conflicts)
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/conflicts`,
          value: updatedProposal.conflicts.map(
            (replaceAbleDocument) => new ReplaceableDocumentAPIRequest(replaceAbleDocument).payload,
          ),
        },
        ...this.payload,
      ];
    }

    if (
      Masked.isNotMasked(updatedProposal.conflictsCheckWaived) &&
      Masked.isNotMasked(originalProposal.conflictsCheckWaived) &&
      !updatedProposal.conflictsCheckWaived.isEqualTo(originalProposal.conflictsCheckWaived)
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/conflictsCheckWaived`,
          value: updatedProposal.conflictsCheckWaived,
        },
        ...this.payload,
      ];
    }
  }

  private populateProposalDiscountDiffPatch(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    if (
      Masked.isNotMasked(updatedProposal.discount) &&
      Masked.isNotMasked(originalProposal.discount) &&
      !updatedProposal.discount.isEqualTo(originalProposal.discount)
    ) {
      this.payload = [
        { op: "replace", path: `${pathPrefix}/discount`, value: updatedProposal.discount },
        ...this.payload,
      ];
    }
  }

  private populateProposalTemplateDiffPatches(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    if (!_.isEqual(updatedProposal.clientTeamTemplateIds, originalProposal.clientTeamTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/clientTeamTemplateIds`,
          value: updatedProposal.clientTeamTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }

    if (!_.isEqual(updatedProposal.vendorTeamTemplateIds, originalProposal.vendorTeamTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/vendorTeamTemplateIds`,
          value: updatedProposal.vendorTeamTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }

    if (!_.isEqual(updatedProposal.clientFeeScheduleTemplateIds, originalProposal.clientFeeScheduleTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/clientFeeScheduleTemplateIds`,
          value: updatedProposal.clientFeeScheduleTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }

    if (!_.isEqual(updatedProposal.clientFeeScheduleTemplateIds, originalProposal.clientFeeScheduleTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/clientFeeScheduleTemplateIds`,
          value: updatedProposal.clientFeeScheduleTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }

    if (!_.isEqual(updatedProposal.vendorFeeScheduleTemplateIds, originalProposal.vendorFeeScheduleTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/vendorFeeScheduleTemplateIds`,
          value: updatedProposal.vendorFeeScheduleTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }

    if (!_.isEqual(updatedProposal.vendorFeeScheduleTemplateIds, originalProposal.vendorFeeScheduleTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/vendorFeeScheduleTemplateIds`,
          value: updatedProposal.vendorFeeScheduleTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }
  }
}

export { SaveProposalAPIRequest, ShareProposalAPIRequest };
